// SaveDraftDialog.tsx - Modal dialog for saving drafts with custom names
import React, { useState, useEffect } from 'react';

interface SaveDraftDialogProps {
  isOpen: boolean;
  suggestedName: string;
  onClose: () => void;
  onSave: (draftName: string) => void;
  isLoading?: boolean;
}

export const SaveDraftDialog: React.FC<SaveDraftDialogProps> = ({
  isOpen,
  suggestedName,
  onClose,
  onSave,
  isLoading = false
}) => {
  const [draftName, setDraftName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setDraftName(suggestedName);
      setError('');
    }
  }, [isOpen, suggestedName]);

  const handleSave = () => {
    if (!draftName.trim()) {
      setError('Please enter a name for the draft');
      return;
    }

    if (draftName.trim().length < 3) {
      setError('Draft name must be at least 3 characters long');
      return;
    }

    if (draftName.trim().length > 255) {
      setError('Draft name must be less than 255 characters');
      return;
    }

    onSave(draftName.trim());
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        width: '90%',
        maxWidth: '500px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: '#1e293b'
          }}>
            Save Draft
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#64748b',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              padding: '4px',
              borderRadius: '4px',
              lineHeight: 1,
              opacity: isLoading ? 0.5 : 1
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ marginBottom: '24px' }}>
          <p style={{
            margin: '0 0 16px 0',
            fontSize: '14px',
            color: '#64748b',
            lineHeight: '1.5'
          }}>
            Give your draft a meaningful name so you can easily find it later.
          </p>

          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '8px'
          }}>
            Draft Name *
          </label>

          <input
            type="text"
            value={draftName}
            onChange={(e) => {
              setDraftName(e.target.value);
              setError('');
            }}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            placeholder="Enter a name for your draft"
            autoFocus
            style={{
              width: '100%',
              padding: '12px',
              border: error ? '2px solid #ef4444' : '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#1e293b',
              backgroundColor: isLoading ? '#f8fafc' : 'white',
              cursor: isLoading ? 'not-allowed' : 'text',
              outline: 'none',
              transition: 'border-color 0.2s ease',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => {
              if (!error) {
                e.target.style.borderColor = '#3b82f6';
              }
            }}
            onBlur={(e) => {
              if (!error) {
                e.target.style.borderColor = '#d1d5db';
              }
            }}
          />

          {error && (
            <div style={{
              marginTop: '8px',
              padding: '8px 12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>⚠️</span>
              {error}
            </div>
          )}

          <div style={{
            marginTop: '8px',
            fontSize: '12px',
            color: '#9ca3af'
          }}>
            {draftName.length}/255 characters
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              backgroundColor: 'transparent',
              color: '#64748b',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.5 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={isLoading || !draftName.trim()}
            style={{
              padding: '10px 20px',
              backgroundColor: isLoading || !draftName.trim() ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isLoading || !draftName.trim() ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            {isLoading ? (
              <>
                <div style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid #ffffff40',
                  borderTop: '2px solid #ffffff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Saving...
              </>
            ) : (
              'Save Draft'
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};